/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { Controller } from "react-hook-form";

import { ChoiceGroup, Icon, Label, TooltipHost } from "@fluentui/react";

import { FlexCol, FlexRow } from "Components/Common/Layout/Layout";
import { HookErrorMessage } from "Components/ReactHookForm/HookErrorMessage/HookErrorMessage";

import { IRadioSelectControllerProps } from "./IRadioSelectControllerProps";
import { choiceGroupStyles, getIconStyles } from "./RadioSelectController.styles";

/**
 * RadioSelectController.
 * @component
 * @param {IRadioSelectControllerProps} props The component props.
 * @returns {React.ReactElement} RadioSelectController.
 */
const RadioSelectController: (props: IRadioSelectControllerProps) => React.ReactElement = (
  props: IRadioSelectControllerProps
): React.ReactElement => {
  const {
    inputName,
    labelDisplayName,
    toolTipDisplayText,
    control,
    isDisabled,
    errors,
    options,
    onChange,
    isRequired,
  } = props;

  return (
    <FlexCol>
      <FlexRow>
        <Label htmlFor={inputName} required={isRequired}>
          {labelDisplayName}
        </Label>
        {!!toolTipDisplayText && (
          <TooltipHost id={inputName} content={toolTipDisplayText || labelDisplayName}>
            <Icon
              aria-label="information"
              iconName="info"
              role="img"
              styles={getIconStyles(!!isRequired)}
              tabIndex={0}
            />
          </TooltipHost>
        )}
      </FlexRow>
      <FlexRow>
        <Controller
          name={inputName}
          control={control}
          //error={errors}
          render={({ field }): JSX.Element => {
            return (
              <ChoiceGroup
                {...field}
                aria-labelledby={labelDisplayName}
                styles={choiceGroupStyles}
                disabled={isDisabled}
                selectedKey={field.value}
                onChange={(e, option): void => {
                  field.onChange(option?.key);
                  !!onChange && !!option && onChange(e, option);
                }}
                defaultSelectedKey={"0"}
                options={options}
              />
            );
          }}
        />
      </FlexRow>
      <HookErrorMessage errors={errors} inputName={inputName} />
    </FlexCol>
  );
};

export default RadioSelectController;
